import { Box, CircularProgress, Tooltip, Typography } from '@mui/material'
import VisibilityIcon from '@mui/icons-material/Visibility'
import BarChartIcon from '@mui/icons-material/BarChart'
import TrendingUpIcon from '@mui/icons-material/TrendingUp'
import { useTranslation } from 'react-i18next'
import { formatNumber } from '../../../../utils/Utils'

const iconMap = {
  trackingVisibility: <VisibilityIcon sx={{ color: 'info.main' }} />,
  trackingPosition: <TrendingUpIcon sx={{ color: 'success.main' }} />,
  trackingNumKeywords: <BarChartIcon sx={{ color: 'warning.main' }} />
};

interface props {
  title: string;
  currentValue: number;
  beforeValue: number;
  tooltip?: string;
  isLoading?: boolean;
  decimals?: number;
}

const TrackingKeywordsSummary = ({ title, currentValue, beforeValue, tooltip, isLoading, decimals=2 }) => {
  const { t } = useTranslation()

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 110 }}>
        <CircularProgress />
      </Box>
    );
  }

  const current = typeof currentValue === 'number' && !isNaN(currentValue) ? currentValue : 0;
  const before = typeof beforeValue === 'number' && !isNaN(beforeValue) ? beforeValue : 0;

  const difference = current - before;
  const percentageChange = before !== 0 ? (difference / Math.abs(before)) * 100 : (current !== 0 ? 100 : 0);

  const ChangeIndicator = () => {
    if (isNaN(difference)) return null;     //return difference>0?'⬆':difference<0?'⬇':'';

    if (before === 0 && current > 0) {
      return (
        <Typography variant="body2" color="success.main" sx={{ display: 'flex', alignItems: 'center', mt: 1, justifyContent: 'center' }}>
         ⬆{t('trackingKeywordSummaryNew')}
        </Typography>
      );
    }

    if (difference === 0) {
      return (
        <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', mt: 1, justifyContent: 'center' }}>
          {t('trackingKeywordSummaryWithoutChanges')}
        </Typography>
      );
    }


    const isPositive = difference > 0;
    return (
      <Typography variant="body2" color={isPositive ? 'success.main' : 'error.main'} sx={{ display: 'flex', alignItems: 'center', mt: 1, justifyContent: 'center' }}>
        {isPositive ? <Typography sx={{ mr: 0.5, fontSize: '1rem' }} color='green'>⬆</Typography> : <Typography sx={{ mr: 0.5, fontSize: '1rem' }} color='red'>⬇</Typography>}
        {`${percentageChange.toFixed(1)}%`}
      </Typography>
    );
  };

  return (
    <Tooltip title={tooltip || ''} arrow followCursor >
      <Box sx={{ textAlign: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mb: 1 }}>
          {title && iconMap[title]}
          <Typography variant="subtitle1" color="text.secondary" sx={{ fontWeight: 500 }}>{t(title)}</Typography>
        </Box>
        <Typography variant="h4" component="div" sx={{ fontWeight: 'bold' }}>
          {formatNumber(current, decimals )}
        </Typography>
        <Box sx={{ minHeight: '27px' }}>
          <ChangeIndicator />
        </Box>
      </Box>
    </Tooltip>
  );
};

export default TrackingKeywordsSummary;